import React, { useEffect, useState } from "react";
import {
  Pencil,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Grid3x3,
  Package2,
  EyeOff,
  ChevronDown,
  ChevronRight,
  User,
  History,
  LayoutGrid,
  Wifi,
  RefreshCw,
  Combine,
  Printer,
  Sticker,
  FileText,
  Building2,
  TrendingUp,
  TrendingDown,
  ClipboardCheck,
  Ghost,
  Link2,
  Copy,
  Check,
  AlertCircle,
} from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { TableCell, TableRow, Table, TableBody, TableHead, TableHeader } from "../ui/table";
import { BARQUETTES_PER_EMPLACEMENT } from "./constants";
import CorporateParticipationSection from "./CorporateParticipationSection";

export default function DistributorRow({
  d,
  lots,
  productMap,
  expanded,
  onToggleExpand,
  onOpenEmps,
  onEdit,
  onDelete,
  onMerge,
  onSyncNayax,
  onCleanPhantomRails,
  onNavigateHistory,
  onNavigateSlots,
  onNavigateRestock,
  onPrintMenu,
  onPrintMenuCompact,
  onPrintScavAnnouncement,
  syncingId,
  nayaxConfigured,
  nayaxSummary,
}) {
  const isClient = d.type === "CLIENT";
  const empCount = (d.emplacements || []).length;
  const caps = d.emplacement_capacities || {};
  const barquettes = (d.emplacements || []).reduce(
    (s, n) => s + (caps[String(n)] || BARQUETTES_PER_EMPLACEMENT),
    0
  );
  const breakdown = getBarquetteBreakdown(lots, productMap, d.id, isClient);
  const clientLotsCount = isClient
    ? lots.filter((l) => l.distributor_id === d.id && (l.quantity || 0) > 0).length
    : 0;

  return (
    <React.Fragment>
      <TableRow data-testid={`distributor-row-${d.id}`}>
        <TableCell className="font-mono text-sm">
          {d.code ? <Badge variant="outline">{d.code}</Badge> : "—"}
        </TableCell>
        <TableCell className="font-medium">
          <button
            onClick={onToggleExpand}
            className="flex items-center gap-1 hover:text-[#1F4E3D] -ml-5 text-left"
            data-testid={`expand-distributor-${d.id}`}
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-[#575A57]" />
            ) : (
              <ChevronRight className="w-4 h-4 text-[#575A57]" />
            )}
            <span>{d.name}</span>
            {isClient && !d.is_laboratory && (
              <Badge
                variant="outline"
                className="bg-[#F1EAF8] border-[#C9B5E0] text-[#5B3A8C] text-[10px] gap-1 ml-1"
              >
                <User className="w-3 h-3" /> Client
              </Badge>
            )}
            {d.is_laboratory && (
              <Badge
                variant="outline"
                className="bg-[#E7F0F5] border-[#95B8C9] text-[#164D63] text-[10px] gap-1 ml-1"
                title="Laboratoire d'analyse HACCP — les BL déclenchent la création automatique de tests"
                data-testid="badge-laboratory"
              >
                🔬 Laboratoire
              </Badge>
            )}
            {isClient && d.price_tier && d.price_tier !== "direct" && (
              <Badge
                variant="outline"
                className="bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F] text-[10px] ml-1"
                title="Niveau tarifaire"
              >
                {d.price_tier === "distributor" ? "Tarif B2B" : "Tarif Internet"}
              </Badge>
            )}
            {d.is_corporate && (
              <Badge
                variant="outline"
                className="bg-[#F1EAF8] border-[#C9B5E0] text-[#5B3A8C] text-[10px] gap-1 ml-1"
                title="Machine subventionnée par entreprise hôte"
              >
                <Building2 className="w-3 h-3" /> Corporate
              </Badge>
            )}
          </button>
        </TableCell>
        <TableCell>
          {isClient ? (
            <div className="text-left -my-1">
              <div className="flex items-center gap-1.5 text-sm font-medium text-[#1A1C1A]">
                <Package2 className="w-3.5 h-3.5 text-[#A3A6A3]" />
                {clientLotsCount > 0
                  ? `${clientLotsCount} barquette${clientLotsCount > 1 ? "s" : ""}`
                  : "Sans stock"}
              </div>
              <div className="text-[10px] text-[#A3A6A3] mt-0.5">Réfrigérateur · pas d'emplacement</div>
            </div>
          ) : d.hide_emplacement_grid ? (
            <div className="text-left -my-1" data-testid={`capacity-readonly-${d.id}`}>
              <div className="flex items-center gap-1.5 text-sm font-medium text-[#1A1C1A]">
                <Grid3x3 className="w-3.5 h-3.5 text-[#A3A6A3]" />
                {empCount} empl.
                <EyeOff className="w-3 h-3 text-[#A3A6A3] ml-0.5" />
              </div>
              <div className="flex items-center gap-1 text-xs text-[#575A57] mt-0.5">
                <Package2 className="w-3 h-3" />
                {breakdown.total > 0 ? `${breakdown.total} occ. / ${barquettes}` : `${barquettes} barquettes`}
              </div>
              {breakdown.byProduct.length > 0 && (
                <div className="text-[10px] text-[#575A57] mt-0.5 max-w-[160px] truncate">
                  {breakdown.byProduct
                    .slice(0, 2)
                    .map(([name, info]) => `${info.count}× ${name}`)
                    .join(" · ")}
                  {breakdown.byProduct.length > 2 && ` +${breakdown.byProduct.length - 2}`}
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={onOpenEmps}
              className="text-left hover:bg-[#F0F0EB] -mx-2 -my-1 px-2 py-1 rounded transition-colors group"
              data-testid={`open-emplacements-${d.id}`}
            >
              <div className="flex items-center gap-1.5 text-sm font-medium text-[#1A1C1A]">
                <Grid3x3 className="w-3.5 h-3.5 text-[#1F4E3D]" />
                {empCount} empl.
              </div>
              <div className="flex items-center gap-1 text-xs text-[#575A57] mt-0.5">
                <Package2 className="w-3 h-3" />
                {breakdown.total > 0 ? `${breakdown.total} occ. / ${barquettes}` : `${barquettes} barquettes`}
                <span className="ml-1 text-[10px] text-[#849F90] group-hover:text-[#1F4E3D]">· gérer</span>
              </div>
              {breakdown.byProduct.length > 0 && (
                <div className="text-[10px] text-[#575A57] mt-0.5 max-w-[160px] truncate">
                  {breakdown.byProduct
                    .slice(0, 2)
                    .map(([name, info]) => `${info.count}× ${name}`)
                    .join(" · ")}
                  {breakdown.byProduct.length > 2 && ` +${breakdown.byProduct.length - 2}`}
                </div>
              )}
            </button>
          )}
        </TableCell>
        <TableCell className="text-sm">{d.contact_name || "—"}</TableCell>
        <TableCell className="text-sm">
          {d.email && (
            <div className="flex items-center gap-1.5">
              <Mail className="w-3 h-3 text-[#575A57]" />
              {d.email}
            </div>
          )}
          {d.phone && (
            <div className="flex items-center gap-1.5">
              <Phone className="w-3 h-3 text-[#575A57]" />
              {d.phone}
            </div>
          )}
          {!d.email && !d.phone && "—"}
        </TableCell>
        <TableCell className="text-sm">
          {(d.postal_code || d.city) && (
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3 h-3 text-[#575A57]" />
              {d.postal_code} {d.city}
            </div>
          )}
          {d.country && <div className="text-xs text-[#575A57] ml-4">{d.country}</div>}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-1">
            {d.nayax_machine_id && d.type === "AUTOMATIQUE" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onSyncNayax(d.id)}
                disabled={syncingId === d.id || !nayaxConfigured}
                title={
                  nayaxConfigured
                    ? formatSyncTooltip(d.nayax_machine_id, nayaxSummary)
                    : "NAYAX_API_KEY non configurée — voir Paramètres"
                }
                data-testid={`sync-nayax-${d.id}`}
                className="relative"
              >
                {syncingId === d.id ? (
                  <RefreshCw className="w-4 h-4 animate-spin text-[#1F4E3D]" />
                ) : (
                  <Wifi className={`w-4 h-4 ${nayaxConfigured ? "text-[#1F4E3D]" : "text-[#A3A6A3]"}`} />
                )}
                {nayaxSummary?.last_sync_at && (
                  <span
                    className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${
                      nayaxSummary.last_had_error ? "bg-[#C05C47]" : "bg-[#1F4E3D]"
                    }`}
                    data-testid={`sync-nayax-status-${d.id}`}
                  />
                )}
              </Button>
            )}
            {d.type === "AUTOMATIQUE" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onCleanPhantomRails(d)}
                title="Nettoyer les plats fantômes (rails pointant vers des produits archivés/introuvables)"
                data-testid={`phantom-rails-${d.id}`}
              >
                <Ghost className="w-4 h-4 text-[#8C6B2F]" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onNavigateRestock}
              title="Créer une tournée de réappro pour ce distributeur"
              data-testid={`restock-distributor-${d.id}`}
            >
              <ClipboardCheck className="w-4 h-4 text-[#1F4E3D]" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onNavigateSlots}
              title="Vue grille des slots (DLC)"
              data-testid={`slots-distributor-${d.id}`}
            >
              <LayoutGrid className="w-4 h-4 text-[#1F4E3D]" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onNavigateHistory}
              title="Historique d'activité"
              data-testid={`history-distributor-${d.id}`}
            >
              <History className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onMerge}
              title="Fusionner un autre dépôt dans celui-ci"
              data-testid={`merge-distributor-${d.id}`}
            >
              <Combine className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onPrintMenu(d)}
              title="Fiche partenaire (PDF A4 multi-pages, photos et noms)"
              data-testid={`print-menu-${d.id}`}
            >
              <Printer className="w-4 h-4 text-[#8C6B2F]" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onPrintMenuCompact(d)}
              title="Fiche vitrine compacte (PDF A4 recto seul, à coller sur le côté du distributeur)"
              data-testid={`print-menu-compact-${d.id}`}
            >
              <Sticker className="w-4 h-4 text-[#1F4E3D]" />
            </Button>
            {onPrintScavAnnouncement && !d.is_internal_stock && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onPrintScavAnnouncement(d)}
                title="Formulaire SCAV — annonce entreprise (denrées alimentaires, art. 20/62 ODAIOUS)"
                data-testid={`print-scav-${d.id}`}
              >
                <FileText className="w-4 h-4 text-[#8C6B2F]" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`edit-distributor-${d.id}`}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onDelete} data-testid={`delete-distributor-${d.id}`}>
              <Trash2 className="w-4 h-4 text-[#C05C47]" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-[#F9F9F6] hover:bg-[#F9F9F6]" data-testid={`detail-${d.id}`}>
          <TableCell colSpan={7} className="p-0">
            <div className="px-6 py-5 border-t border-b border-[#E5E5E0]">
              {d.is_corporate && <CorporateRevenuePanel distId={d.id} />}
              {d.type === "AUTOMATIQUE" && <PartnerLinkPanel distId={d.id} distName={d.name} />}
              {/* Widget Écart inexpliqué — Phase 101db */}
              <UnexplainedVariancePanel distId={d.id} distName={d.name} />
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Package2 className="w-4 h-4 text-[#1F4E3D]" />
                  <h3 className="font-heading text-base font-semibold text-[#1A1C1A]">
                    Détail des barquettes — {d.name}
                  </h3>
                </div>
                <div className="text-sm font-mono text-[#575A57]">
                  {isClient
                    ? `${breakdown.total} barquette${breakdown.total > 1 ? "s" : ""} chez ce client`
                    : `${breakdown.total} occupées / ${barquettes} totales`}
                </div>
              </div>
              {breakdown.byProduct.length === 0 ? (
                <div className="text-sm text-[#575A57] py-4 px-4 bg-white border border-[#E5E5E0] rounded">
                  {isClient
                    ? "Aucune barquette assignée à ce client. Transférez des barquettes depuis la page Stocks."
                    : "Aucune barquette occupée. Assignez des lots à des emplacements/barquettes via la page Stocks."}
                </div>
              ) : (
                <div className="bg-white border border-[#E5E5E0] rounded overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
                        <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">
                          Plat
                        </TableHead>
                        <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">
                          Barquettes
                        </TableHead>
                        <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">
                          Lots concernés
                        </TableHead>
                        {!isClient && (
                          <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">
                            Positions
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {breakdown.byProduct.map(([name, info]) => (
                        <TableRow key={name} data-testid={`breakdown-row-${name}`}>
                          <TableCell className="font-medium">
                            <div className="flex flex-col">
                              <span className="text-[#1A1C1A]">{name}</span>
                              {info.sku && (
                                <span className="text-[10px] font-mono text-[#A3A6A3]">{info.sku}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold">{info.count}</TableCell>
                          <TableCell className="text-sm">
                            {Array.from(new Set(info.lots.map((l) => l.lot_number)))
                              .slice(0, 5)
                              .map((ln) => (
                                <Badge key={ln} variant="outline" className="font-mono mr-1 mb-1 text-xs">
                                  {ln}
                                </Badge>
                              ))}
                            {new Set(info.lots.map((l) => l.lot_number)).size > 5 && (
                              <span className="text-xs text-[#575A57]">
                                +{new Set(info.lots.map((l) => l.lot_number)).size - 5}
                              </span>
                            )}
                          </TableCell>
                          {!isClient && (
                            <TableCell className="text-sm font-mono text-[#575A57]">
                              {info.lots.map((l) => `E${l.emplacement_number}·B${l.barquette_index}`).join(", ")}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}

function formatSyncTooltip(machineId, summary) {
  let s = `Synchroniser ventes Nayax (machine ${machineId})`;
  if (summary?.last_sync_at) {
    const dt = new Date(summary.last_sync_at);
    const dateStr = dt.toLocaleString("fr-CH", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
    s += `\nDernière sync : ${dateStr}`;
    s += `\n${summary.last_sales_created || 0} ventes créées · ${summary.last_skipped || 0} doublons skippés`;
    if (summary.last_failed) s += ` · ${summary.last_failed} échec(s)`;
    if (summary.last_source === "scheduler") s += "\n(auto par planificateur)";
  } else {
    s += "\nJamais synchronisé encore";
  }
  return s;
}

function getBarquetteBreakdown(lots, productMap, depotId, isClient) {
  const occupiedLots = lots.filter((l) => {
    if (l.distributor_id !== depotId) return false;
    if ((l.quantity || 0) <= 0) return false;
    if (isClient) return true;
    return l.emplacement_number && l.barquette_index;
  });
  const byProduct = {};
  for (const lot of occupiedLots) {
    const product = productMap[lot.product_id];
    const name = product?.name || "Produit inconnu";
    if (!byProduct[name]) byProduct[name] = { count: 0, lots: [], sku: product?.sku || "" };
    byProduct[name].count += 1;
    byProduct[name].lots.push(lot);
  }
  const entries = Object.entries(byProduct).sort((a, b) => b[1].count - a[1].count);
  return { total: occupiedLots.length, byProduct: entries };
}


// ================== Lien Catalogue Partenaire (Phase 101ep) ==================
// Affiché pour tout distributeur AUTOMATIQUE — permet à l'hébergeur (Sion
// Piscine, etc.) de consulter le catalogue Maison Thaï (rails + suggérables)
// et proposer une rotation. Indépendant du portail Corporate.
function PartnerLinkPanel({ distId, distName }) {
  const [copied, setCopied] = useState(false);
  const publicUrl = `${window.location.origin}/partenaire/${distId}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement("textarea");
      el.value = publicUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className="mb-4 rounded-md border border-[#E8D3A8] bg-gradient-to-br from-[#FBF4E9] to-[#F6EAD0] px-4 py-3"
      data-testid={`partner-link-panel-${distId}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="w-3.5 h-3.5 text-[#8C6B2F]" />
        <span className="text-[10px] uppercase tracking-wider text-[#8C6B2F] font-semibold">
          Catalogue Partenaire — lien partageable
        </span>
      </div>
      <p className="text-[11px] text-[#575A57] mb-2 leading-snug">
        Lien lecture seule pour l&apos;hébergeur de <b>{distName}</b> : catalogue
        Maison Thaï avec plats actuellement en machine + plats suggérables pour
        une rotation. Prix public consommateur uniquement.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <code
          className="flex-1 min-w-0 truncate text-[11px] font-mono text-[#7A5628] bg-white/70 px-2 py-1 rounded border border-[#E8D3A8]"
          data-testid={`partner-public-url-${distId}`}
          title={publicUrl}
        >
          {publicUrl}
        </code>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={copyLink}
          className="h-7 text-xs border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#F6EAD0]"
          data-testid={`partner-copy-link-${distId}`}
        >
          {copied ? <><Check className="w-3 h-3 mr-1" /> Copié</> : <><Copy className="w-3 h-3 mr-1" /> Copier</>}
        </Button>
        <a
          href={publicUrl}
          target="_blank"
          rel="noreferrer"
          className="text-[11px] underline text-[#8C6B2F] hover:text-[#735524]"
          data-testid={`partner-open-link-${distId}`}
        >
          Ouvrir ↗
        </a>
      </div>
    </div>
  );
}


function CorporateRevenuePanel({ distId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const publicUrl = `${window.location.origin}/corporate/${distId}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback : sélection manuelle si clipboard bloqué
      const el = document.createElement("textarea");
      el.value = publicUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/distributors/${distId}/corporate-revenue`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Erreur de chargement");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [distId]);

  if (loading) {
    return (
      <div
        className="mb-4 rounded-md border border-[#C9B5E0] bg-[#F8F4FD] px-4 py-3 text-sm text-[#5B3A8C]"
        data-testid={`corporate-revenue-loading-${distId}`}
      >
        Chargement du CA mensuel…
      </div>
    );
  }
  if (error) {
    return (
      <div className="mb-4 rounded-md border border-[#E8B0A0] bg-[#FBEEEA] px-4 py-3 text-sm text-[#A04E2C]">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const guarantee = data.guarantee_chf || 0;
  const revenue = data.revenue_chf || 0;
  const isBelow = data.status === "BELOW";
  const pct = guarantee > 0 ? Math.min(100, (revenue / guarantee) * 100) : 100;

  return (
    <div
      className="mb-5 rounded-md border border-[#C9B5E0] bg-gradient-to-br from-[#F8F4FD] to-[#F1EAF8] px-4 py-4"
      data-testid={`corporate-revenue-panel-${distId}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[#5B3A8C]" />
          <h3 className="font-heading text-sm font-semibold text-[#3D2A5C]">
            Corporate — Suivi CA mensuel ({data.month})
          </h3>
        </div>
        <div
          className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full ${
            isBelow
              ? "bg-[#FBEEEA] text-[#A04E2C] border border-[#E8B0A0]"
              : "bg-[#E8F2EC] text-[#1F4E3D] border border-[#A8C9B6]"
          }`}
          data-testid={`corporate-status-${distId}`}
        >
          {isBelow ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
          {isBelow ? "Sous garantie" : guarantee === 0 ? "Pas de garantie" : "OK"}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#5B3A8C] mb-0.5">
            CA réalisé
          </div>
          <div className="text-lg font-mono font-semibold text-[#1A1C1A]">
            {revenue.toFixed(2)} CHF
          </div>
          <div className="text-[10px] text-[#575A57] mt-0.5">
            {data.units_sold || 0} barquette(s) · {data.sales_count || 0} vente(s)
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#5B3A8C] mb-0.5">
            CA garanti
          </div>
          <div className="text-lg font-mono font-semibold text-[#1A1C1A]">
            {guarantee > 0 ? `${guarantee.toFixed(2)} CHF` : "—"}
          </div>
          <div className="text-[10px] text-[#575A57] mt-0.5">
            {guarantee > 0 ? "Minimum mensuel négocié" : "Non défini"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#5B3A8C] mb-0.5">
            Delta à facturer
          </div>
          <div
            className={`text-lg font-mono font-semibold ${
              data.delta_to_invoice_chf > 0 ? "text-[#A04E2C]" : "text-[#1F4E3D]"
            }`}
          >
            {data.delta_to_invoice_chf > 0
              ? `+${data.delta_to_invoice_chf.toFixed(2)} CHF`
              : "0.00 CHF"}
          </div>
          <div className="text-[10px] text-[#575A57] mt-0.5">
            {data.corporate_billing_email ? `→ ${data.corporate_billing_email}` : "Aucun email compta défini"}
          </div>
        </div>
      </div>

      {guarantee > 0 && (
        <div className="h-2 bg-white rounded-full overflow-hidden border border-[#C9B5E0]">
          <div
            className={`h-full transition-all duration-500 ${
              isBelow ? "bg-[#C05C47]" : "bg-[#1F4E3D]"
            }`}
            style={{ width: `${pct}%` }}
            data-testid={`corporate-progress-${distId}`}
          />
        </div>
      )}

      {/* Lien public partageable — Phase 101cw */}
      <div className="mt-3 pt-3 border-t border-[#DBC9EE] flex items-center gap-2 flex-wrap">
        <Link2 className="w-3.5 h-3.5 text-[#5B3A8C] flex-shrink-0" />
        <span className="text-[10px] uppercase tracking-wider text-[#5B3A8C] font-semibold">
          Portail Novelis
        </span>
        <code
          className="flex-1 min-w-0 truncate text-[11px] font-mono text-[#3D2A5C] bg-white/60 px-2 py-1 rounded border border-[#DBC9EE]"
          data-testid={`corporate-public-url-${distId}`}
          title={publicUrl}
        >
          {publicUrl}
        </code>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={copyLink}
          className="h-7 text-xs border-[#5B3A8C] text-[#5B3A8C] hover:bg-[#F1EAF8]"
          data-testid={`corporate-copy-link-${distId}`}
        >
          {copied ? <><Check className="w-3 h-3 mr-1" /> Copié</> : <><Copy className="w-3 h-3 mr-1" /> Copier</>}
        </Button>
        <a
          href={publicUrl}
          target="_blank"
          rel="noreferrer"
          className="text-[11px] underline text-[#5B3A8C] hover:text-[#3D2A5C]"
          data-testid={`corporate-open-link-${distId}`}
        >
          Ouvrir ↗
        </a>
      </div>

      {/* Gestion des contacts autorisés (portail authentifié) — Phase 101cx-corp */}
      <CorporateContactsSection distId={distId} />

      {/* Rapports de participation (facturation QR-Bill mensuelle) — Phase 101du.3 */}
      <CorporateParticipationSection distId={distId} />
    </div>
  );
}

// ================== Gestion des contacts corporate ==================
function CorporateContactsSection({ distId }) {
  const [contacts, setContacts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "" });
  const [submitting, setSubmitting] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/distributors/${distId}/corporate-contacts`)
      .then((r) => { if (!cancelled) setContacts(r.data); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Erreur");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [distId, refreshTick]);

  const reload = () => setRefreshTick((t) => t + 1);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/distributors/${distId}/corporate-contacts`, {
        email: form.email.trim(),
        name: form.name.trim(),
        role: form.role.trim(),
        send_invite: true,
      });
      setForm({ email: "", name: "", role: "" });
      setShowForm(false);
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  const resendInvite = async (id, email) => {
    try {
      await api.post(`/corporate-contacts/${id}/resend-invite`);
      alert(`Invitation renvoyée à ${email}`);
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur lors de l'envoi");
    }
  };

  const toggleDisabled = async (contact) => {
    try {
      await api.patch(`/corporate-contacts/${contact.id}`, { disabled: !contact.disabled });
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    }
  };

  const deleteContact = async (id, email) => {
    if (!window.confirm(`Supprimer définitivement le contact ${email} ?`)) return;
    try {
      await api.delete(`/corporate-contacts/${id}`);
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#DBC9EE]" data-testid={`corporate-contacts-section-${distId}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="text-[10px] uppercase tracking-wider text-[#5B3A8C] font-semibold">
          Contacts autorisés (portail auth.)
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setShowForm((v) => !v)}
          className="h-6 text-[10px] border-[#5B3A8C] text-[#5B3A8C] hover:bg-[#F1EAF8]"
          data-testid={`corporate-contact-add-toggle-${distId}`}
        >
          {showForm ? "Fermer" : "+ Ajouter"}
        </Button>
      </div>

      {loading && <div className="text-[11px] text-[#5B3A8C]">Chargement…</div>}
      {error && <div className="text-[11px] text-[#A04E2C]">{error}</div>}

      {showForm && (
        <form
          onSubmit={submitContact}
          className="mb-2 p-2.5 bg-white/70 rounded border border-[#DBC9EE] space-y-1.5"
          data-testid={`corporate-contact-form-${distId}`}
        >
          <div className="grid grid-cols-3 gap-1.5">
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="col-span-3 text-[11px] px-2 py-1 border border-[#DBC9EE] rounded"
              required
              data-testid={`corporate-contact-email-${distId}`}
            />
            <input
              type="text"
              placeholder="Nom"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="col-span-2 text-[11px] px-2 py-1 border border-[#DBC9EE] rounded"
              required
              data-testid={`corporate-contact-name-${distId}`}
            />
            <input
              type="text"
              placeholder="Rôle (RH, Compta…)"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="text-[11px] px-2 py-1 border border-[#DBC9EE] rounded"
              data-testid={`corporate-contact-role-${distId}`}
            />
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={submitting}
            className="h-7 text-[11px] bg-[#5B3A8C] hover:bg-[#3D2A5C] text-white w-full"
            data-testid={`corporate-contact-submit-${distId}`}
          >
            {submitting ? "Envoi…" : "Créer + envoyer l'invitation"}
          </Button>
        </form>
      )}

      {contacts && contacts.length === 0 && !showForm && (
        <div className="text-[11px] text-[#575A57] italic">
          Aucun contact autorisé. Ajoutez-en pour permettre à un responsable de se connecter à son espace.
        </div>
      )}

      {contacts && contacts.length > 0 && (
        <div className="space-y-1">
          {contacts.map((c) => {
            const status = c.disabled
              ? { txt: "Désactivé", cls: "bg-[#F0F0EB] text-[#575A57] border-[#DDD]" }
              : c.activated_at
              ? { txt: "Actif", cls: "bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]" }
              : { txt: "Invitation en attente", cls: "bg-[#FFF7DB] text-[#8C6B2F] border-[#E5DBC1]" };
            return (
              <div
                key={c.id}
                className="flex items-center gap-2 flex-wrap p-2 bg-white/60 rounded border border-[#DBC9EE] text-[11px]"
                data-testid={`corporate-contact-row-${c.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1A1C1A] truncate">
                    {c.name}
                    {c.role && <span className="text-[#8C6B2F] ml-1.5">· {c.role}</span>}
                  </div>
                  <div className="text-[#575A57] truncate">{c.email}</div>
                </div>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold ${status.cls}`}>
                  {status.txt}
                </span>
                <div className="flex items-center gap-1">
                  {!c.activated_at && (
                    <button
                      type="button"
                      onClick={() => resendInvite(c.id, c.email)}
                      className="text-[10px] text-[#5B3A8C] underline hover:text-[#3D2A5C]"
                      data-testid={`corporate-contact-resend-${c.id}`}
                    >
                      Renvoyer
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleDisabled(c)}
                    className="text-[10px] text-[#575A57] underline hover:text-[#1A1C1A]"
                    data-testid={`corporate-contact-toggle-${c.id}`}
                  >
                    {c.disabled ? "Activer" : "Désactiver"}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteContact(c.id, c.email)}
                    className="text-[10px] text-[#A04E2C] underline hover:text-[#7A1F1F]"
                    data-testid={`corporate-contact-delete-${c.id}`}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ================== Widget Écart inexpliqué (Phase 101db) ==================
function UnexplainedVariancePanel({ distId, distName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/distributors/${distId}/unexplained-variance`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Erreur"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [distId]);

  if (loading) {
    return (
      <div className="mb-4 p-4 bg-[#F9F9F6] border border-[#E5E5E0] rounded-md" data-testid={`variance-loading-${distId}`}>
        <div className="text-xs text-[#575A57]">Chargement écart…</div>
      </div>
    );
  }
  if (error || !data) return null;

  const total = data.total_unexplained || 0;
  const problematic = (data.products || []).filter((p) => Math.abs(p.unexplained) >= 1);
  const isBad = Math.abs(total) >= 1;

  return (
    <div
      className={`mb-4 p-4 rounded-md border ${
        isBad ? "bg-[#FBEEEA] border-[#E8B0A0]" : "bg-[#F0F7F1] border-[#C4DDCB]"
      }`}
      data-testid={`variance-panel-${distId}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <AlertCircle className={`w-4 h-4 ${isBad ? "text-[#A04E2C]" : "text-[#1F4E3D]"}`} />
          <div>
            <div className={`text-sm font-semibold ${isBad ? "text-[#7A2D1C]" : "text-[#1F4E3D]"}`}>
              {isBad
                ? `⚠️ ${Math.abs(total).toFixed(0)} barquette${Math.abs(total) > 1 ? "s" : ""} non tracée${Math.abs(total) > 1 ? "s" : ""}`
                : "Écart tracé — OK"}
            </div>
            <div className="text-[10px] text-[#575A57] mt-0.5">
              Écart = stock actuel − (entrées − ventes − retirées) · mois en cours
              {isBad && " · signal potentiel de sortie non déclarée"}
            </div>
          </div>
        </div>
        {problematic.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setExpanded((v) => !v)}
            className={isBad ? "text-[#A04E2C] border-[#E8B0A0]" : "text-[#1F4E3D] border-[#C4DDCB]"}
            data-testid={`variance-toggle-${distId}`}
          >
            {expanded ? "Masquer détail" : `Voir détail (${problematic.length})`}
          </Button>
        )}
      </div>

      {expanded && problematic.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#E5DBC1]" data-testid={`variance-detail-${distId}`}>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#575A57]">
                <th className="text-left py-1 font-medium">Produit</th>
                <th className="text-right py-1 font-medium w-16">Entrées</th>
                <th className="text-right py-1 font-medium w-16">Ventes</th>
                <th className="text-right py-1 font-medium w-20">Retirées</th>
                <th className="text-right py-1 font-medium w-16">Stock</th>
                <th className="text-right py-1 font-medium w-20">Écart</th>
              </tr>
            </thead>
            <tbody>
              {problematic.map((p) => (
                <tr key={p.product_id} className="border-t border-[#F0F0EB]">
                  <td className="py-1">
                    <div className="font-medium">{p.product_name}</div>
                    {p.product_sku && <div className="text-[9px] text-[#A3A6A3]">{p.product_sku}</div>}
                  </td>
                  <td className="py-1 text-right font-mono">{p.entered.toFixed(0)}</td>
                  <td className="py-1 text-right font-mono">{p.sold.toFixed(0)}</td>
                  <td className="py-1 text-right font-mono">{p.withdrawn.toFixed(0)}</td>
                  <td className="py-1 text-right font-mono">{p.current_stock.toFixed(0)}</td>
                  <td className={`py-1 text-right font-mono font-semibold ${p.unexplained < 0 ? "text-[#A04E2C]" : "text-[#1F4E3D]"}`}>
                    {p.unexplained > 0 ? "+" : ""}{p.unexplained.toFixed(0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 text-[10px] text-[#575A57] italic">
            Un écart négatif = manque de stock inexpliqué (potentielle sortie illicite).
            Un écart positif = surplus à investiguer (mouvement non enregistré, refill non tracé).
          </div>
        </div>
      )}
    </div>
  );
}
